import config from '../config';
import { APICallState, APICallStore } from './api_call_store';
import {
    APIError,
    APITypes,
    StreemAPI,
    StreemApiGetCompanyRoomOutcomeConfigResponse,
} from '@streem/api';
import { streem } from 'streem-sdk-protobuf';
import { autorun, computed, IReactionDisposer } from 'mobx';
import {
    createCompanySettingsDictionary,
    createSourceSettingsDictionary,
    DisplayableSettingsDictionary,
    Setting,
    SettingMeta,
    SettingsDictionary,
    SourceSettingsDictionary,
} from '../util/company_settings';
import appLogger from '../util/logging/app_logger';
import { CancellablePromise } from '../types/project.types';
import { invariant } from '@streem/toolbox';
import { contextValues } from '@streem/logger';

export const displayableSettings: DisplayableSettingsDictionary = {
    CUSTOMER_TS_AND_CS_URL: {
        label: "Additional T&C's for Customer",
        description: "Enter an additional T&C's URL for #COMPANY if needed.",
        superAdmin: true,
        order: 0,
        fieldType: 'URL',
        testId: 'customer-ts-and-cs-url',
    },
    PRO_TS_AND_CS_URL: {
        label: "Additional T&C's for Pro",
        description: "Enter an additional T&C's URL for #COMPANY if needed.",
        superAdmin: true,
        order: 1,
        fieldType: 'URL',
        testId: 'pro-ts-and-cs-url',
    },
    CUSTOMER_PRIVACY_POLICY_URL: {
        label: 'Privacy Policy',
        description: 'Enter a privacy policy URL for #COMPANY if needed.',
        superAdmin: true,
        order: 2,
        fieldType: 'URL',
        testId: 'customer-privacy-policy-url',
    },
    PRO_SURVEY_URL: {
        label: 'Pro Post-Streem Survey',
        description: 'Enter a URL for a short survey for a pro to take after a Streem call',
        superAdmin: true,
        order: 3,
        fieldType: 'URL',
        testId: 'pro-survey-url',
    },
    GPS_ENABLED: {
        label: 'GPS',
        description: 'Allow for agents to use GPS requests during live calls.',
        superAdmin: false,
        order: 5,
        testId: 'gps-enabled',
    },
    ANONYMOUS_SHARE_ENABLED: {
        label: 'Anonymous Share',
        description:
            'Allow users to share room data including streemshots and recordings with anyone outside the company.',
        superAdmin: false,
        order: 6,
        testId: 'anonymous-share-enabled',
    },
    SHOW_CUSTOMER_REDIRECT_URL: {
        label: 'Show Final Redirect URL',
        description:
            'Display the Final Redirect URL customization and allow Company Admins to enable or disable this feature',
        superAdmin: true,
        order: 8,
        testId: 'show-customer-redirect-url',
    },
    UNRELEASED_FEATURES_ENABLED: {
        label: 'Unreleased Features',
        description: 'Enables unreleased features to be displayed',
        superAdmin: true,
        order: 10,
        testId: 'unreleased-features-enabled',
    },
    FACE_TO_FACE_MODE_ENABLED: {
        label: 'Two-Way Video',
        description:
            "Enabling this starts calls with Expert's front-facing camera turned on for all calls (assuming they have a camera).",
        superAdmin: true,
        order: 11,
        testId: 'two-way-video-enabled',
    },
    SHOW_ANONYMOUS_SHARE_ENABLED: {
        label: 'Show Anonymous Share',
        description:
            'Display the Anonymous Share setting and allow Company Admins to enable or disable this feature',
        superAdmin: true,
        order: 13,
        testId: 'show-anonymous-share-enable',
    },
    SHOW_GPS_ENABLED: {
        label: 'Show GPS',
        description:
            'Display the GPS setting and allow Company Admins to enable or disable this feature',
        superAdmin: true,
        order: 14,
        testId: 'show-gps-enabled',
    },
    SHOW_CUSTOMER_COMPANY_WATERMARK: {
        label: 'Show Company Watermark Feature',
        description:
            'Display the Company Watermark customization and allow Company Admins to enable or disable this feature.',
        superAdmin: true,
        order: 15,
        testId: 'show-customer-company-watermark',
    },
    POST_CALL_ANNOTATIONS_ENABLED: {
        label: 'Enable Post-Call Annotations',
        description:
            'Allows Streemshots to be edited after a call has ended and saved as revisions that can be shared, or reverted as needed.',
        superAdmin: true,
        order: 16,
        testId: 'post-call-annotations-enabled',
    },
    GROUP_MANAGEMENT_ENABLED: {
        label: 'Enable Group Management',
        description: 'Allows Company Admins to organize and manage experts in groups they create.',
        superAdmin: true,
        order: 18,
        testId: 'group-management-enabled',
    },
    EXPERT_AVAILABILITY_ENABLED: {
        label: 'Enable Expert Availability',
        description: 'Allows users to use group reservations and expert availability statuses.',
        superAdmin: true,
        order: 19,
        testId: 'expert-availability-enabled',
    },
    ROOM_TTL_DAYS: {
        label: 'Data Retention Policy',
        description:
            'This is the number of days after a call that all the information about that call will be deleted',
        superAdmin: true,
        order: 21,
        testId: 'room-ttl-days',
    },
    CHIME_LOW_BANDWIDTH_OPTIMIZATION_ENABLED: {
        label: 'Chime Low Bandwidth Optimization',
        description: 'Enables low bandwidth optimization for Chime calls',
        superAdmin: true,
        order: -1,
        testId: 'low-bandwidth-optimization-enabled',
    },
    CALL_LOG_FILTER_ENABLED: {
        label: 'Enable Call Log Filtering',
        description: 'Enables Filtering for Call Logs',
        superAdmin: true,
        order: 22,
        testId: 'call-log-filter-setting-enabled',
    },
    AHS_VIRTUAL_EXPERT_ENABLED: {
        label: 'AHS Virtual Expert',
        description: 'Enables AHS Virtual Expert features',
        superAdmin: true,
        order: 23,
        testId: 'ahs-virtual-expert-enabled',
    },
    PREFERRED_CODECS: {
        label: 'Preferred Video Codecs',
        description:
            'Allows users to list the preferred codecs for calls. Must be a comma separated list of codecs.',
        superAdmin: true,
        order: 24,
        testId: 'preferred-codecs',
    },
    ROOM_NOTE_CHAR_LIMIT: {
        label: 'Room Note Character Limit',
        description: 'The character limit for room notes',
        superAdmin: true,
        order: 25,
        testId: 'room-note-char-limit',
        hideToggle: true,
    },
    STREEMSHOT_NOTE_CHAR_LIMIT: {
        label: 'Streemshot Note Character Limit',
        description: 'The character limit for streemshot notes',
        superAdmin: true,
        order: 26,
        testId: 'streemshot-note-char-limit',
        hideToggle: true,
    },
    CALL_DETAIL_TABS_ENABLED: {
        label: 'Enable Tab View in Call Details',
        description: 'Enables tab view in call details for reservations',
        superAdmin: true,
        order: 27,
        testId: 'call-detail-tabs-enabled',
    },
    TRANSCRIPTION_ENABLED: {
        label: 'Transcription Enabled',
        description: 'Enables post-call transcriptions of the audio recording for a Streem Call.',
        order: 28,
        superAdmin: true,
        testId: 'transcription-enabled',
    },
    LOST_CONNECTION_MODAL_ENABLED: {
        label: 'Enable Lost Connection Modal',
        description: 'Display Lost Connection Modal',
        order: 29,
        superAdmin: true,
        testId: 'lost-connection-modal-enabled',
    },
    SEND_NOTE_ENABLED: {
        label: 'Send Note Enabled',
        description: 'Enables expert ability to send note.',
        order: 30,
        superAdmin: true,
        testId: 'send_note-enabled',
    },
    ADVANCED_MEDIA_SETTINGS_ENABLED: {
        label: 'Advanced Media Settings Enabled',
        description: 'Enables media device dropdowns on top nav.',
        order: 31,
        superAdmin: true,
        testId: 'advanced-media-settings-enabled',
    },
    NOTES_EDITOR_ENABLED: {
        label: 'Enable WYSISYG Notes Editor',
        description: 'Enables Notes 2.0 feature.',
        order: 32,
        superAdmin: true,
        testId: 'notes-editor-enabled',
    },
    NOTES_TEMPLATES_ENABLED: {
        label: 'Enable Templates',
        description: 'Enables Templates for Notes 2.0 feature.',
        order: 33,
        superAdmin: true,
        testId: 'notes-templates-enabled',
    },
    HVAC_ELIGIBLE_ENABLED: {
        label: 'Enable hvac eligible and hvac mentioned fields',
        description:
            'Enables hvac eligible in member info for call details and hvac mentioned in room outcome form.',
        order: 34,
        superAdmin: true,
        testId: 'hvac-eligible-enabled',
    },
    TRANSCRIPTION_UI_ENABLED: {
        label: 'Enable transcription ui',
        description:
            'Enables transcription ui to show so expert can view and navigate video in text form',
        order: 35,
        superAdmin: true,
        testId: 'transcription-ui-enabled',
    },
};

export const displayableCustomizations: DisplayableSettingsDictionary = {
    COMPANY_LOGO_URL: {
        label: 'Company Logo Large',
        description: 'A large, full-color logo on a transparent background.',
        order: 0,
        testId: 'company-logo-url',
    },
    COMPANY_WATERMARK_URL: {
        label: 'Company Watermark',
        description: 'A small white logo on a transparent background.',
        order: 1,
        testId: 'company-watermark-url',
    },
    CUSTOMER_REDIRECT_URL: {
        label: 'Final Redirect URL',
        description: 'Customize where your customer is sent after a Streem call has ended.',
        order: 2,
        fieldType: 'URL',
        testId: 'customer-redirect-url',
    },
    CUSTOMER_TS_AND_CS_URL: {
        label: 'Terms and Conditions URL',
        description: "Direct your customers to your company's terms and conditions.",
        order: 4,
        fieldType: 'URL',
        testId: 'customer-ts-and-cs-url',
    },
    SWAC_HIDE_STREEM_BRANDING: {
        label: 'Remove Streem Branding',
        description: 'Remove "Powered by Streem" logo from experience.',
        superAdmin: true,
        order: 5,
        testId: 'hide-streem-branding',
    },
};

export const displayableDevSettings: DisplayableSettingsDictionary = {
    WEB_AR_ENABLED: {
        label: 'AR Tools',
        description: 'Enables AR Tools in EV2 when communicating with an AR-capable device.',
        order: -1,
        testId: 'web-ar-enabled',
    },
    CUSTOMER_SMS_MESSAGE_TEXT: {
        label: 'SMS Message Text',
        description: 'Customize the sms message text sent to the customer',
        order: -1,
        testId: 'customer-sms-message-text',
    },
};

export const displayableBetaSettings: DisplayableSettingsDictionary = {
    ROOM_OUTCOME_FEATURE_ENABLED: {
        label: 'Call Disposition',
        description:
            'Call Disposition allows users the ability to record the reason and resolution of their calls.',
        order: 1,
        testId: 'call-disposition-enabled',
    },
    ALLOW_PAUSE_RESUME_RECORDING: {
        label: 'Allow Pause/Resume Recording',
        description: 'Allows users to pause and resume recordings during a call.',
        order: 2,
        testId: 'allow-pause-resume-recording',
    },
};

/**
 * Load all company settings (including settings that may contain sensitive information)
 * Examples: settings related to the in-call experience
 *
 * Map for display in the Admin views as well as within logic that determines user experience
 */
export class CompanySettingsStore {
    private loadSettingsStore = new APICallStore(
        StreemAPI.companies.listCompanySettings.bind(StreemAPI.companies),
        'listCompanySettings',
    );
    private _roomOutcomeConfig: streem.api.ICompanyRoomOutcomeConfig &
        StreemApiGetCompanyRoomOutcomeConfigResponse = null;
    private disposer: IReactionDisposer | null = null;

    constructor(private readonly companyCode: string) {}

    public connect(): void {
        this.disposer = autorun(() => {
            // Update logger context
            if (this.loadSettingsStore.complete && !this.loadSettingsStore.lastError) {
                appLogger.setContextValue(contextValues.FEATURE_FLAGS, {
                    // add/remove feature flags as needed
                    transcriptionUiEnabledYn: this.transcriptionUiEnabled,
                    transcriptionEnabled: this.transcriptionEnabled,
                    notesTemplatesEnabled: this.notesTemplatesEnabled,
                });
            }
        });
    }

    public disconnect(): void {
        if (this.disposer) {
            this.disposer();
            this.disposer = null;
        }
    }

    public async getDataRetentionPolicyDefault(): Promise<string> {
        const response = await this.getSettingDefinition('ROOM_TTL_DAYS');
        return response._default;
    }

    // denotes completion of an API call from the the APICallStore class
    @computed
    public get complete(): boolean {
        return this.loadSettingsStore.complete;
    }

    @computed
    public get lastError(): APIError {
        return this.loadSettingsStore.lastError;
    }

    @computed
    public get state(): APICallState {
        return this.loadSettingsStore.state;
    }

    @computed
    public get settings(): Setting[] {
        if (!this.loadSettingsStore.response || !this.loadSettingsStore.response.settings) {
            return undefined;
        }
        return this.filterAndSortSettingsForSettingsPage(
            this.loadSettingsStore.response.settings,
            displayableSettings,
        );
    }

    @computed
    public get developerSettings(): Setting[] {
        if (!this.loadSettingsStore.response || !this.loadSettingsStore.response.settings) {
            return undefined;
        }

        return this.loadSettingsStore.response.settings
            .filter(s => s.name)
            .filter(s => !(s.name in displayableSettings))
            .filter(s => !(s.name in displayableCustomizations))
            .filter(s => !(s.name in displayableBetaSettings))
            .map((s, index) => this.toDeveloperSetting(s, index, displayableDevSettings[s.name]));
    }

    @computed
    public get customizations(): Setting[] {
        if (!this.loadSettingsStore.response || !this.loadSettingsStore.response.settings) {
            return undefined;
        }

        return this.filterAndSortSettingsForSettingsPage(
            this.loadSettingsStore.response.settings,
            displayableCustomizations,
        );
    }

    @computed
    public get betaSettings(): Setting[] {
        if (!this.loadSettingsStore.response || !this.loadSettingsStore.response.settings) {
            return undefined;
        }
        return this.filterAndSortSettingsForSettingsPage(
            this.loadSettingsStore.response.settings,
            displayableBetaSettings,
        );
    }

    @computed
    public get chimeNetworkAdaptationEnabled(): boolean {
        return this.companySettingsDictionary?.CHIME_NETWORK_ADAPTATION_ENABLED?.value === 'true';
    }

    @computed
    public get videoProviderVerboseLogging(): string | undefined {
        return this.companySettingsDictionary?.VIDEO_PROVIDER_VERBOSE_LOGGING?.value;
    }

    @computed
    public get companyLogoUrl(): string | undefined {
        return this.companySettingsDictionary?.COMPANY_LOGO_URL?.value;
    }

    @computed
    public get companySurveyUrl(): string | undefined {
        return this.companySettingsDictionary?.PRO_SURVEY_URL?.value;
    }

    @computed
    public get streemSurveyUrl(): string | undefined {
        return (
            this.companySettingsDictionary?.STREEM_SURVEY_URL?.value ??
            config.defaultStreemSurveyUrl
        );
    }

    @computed
    public get proTsAndCsUrl(): string | undefined {
        return this.companySettingsDictionary?.PRO_TS_AND_CS_URL?.value;
    }

    @computed
    public get anonymousShareEnabled(): boolean {
        return this.companySettingsDictionary?.ANONYMOUS_SHARE_ENABLED?.value === 'true';
    }

    @computed
    public get gpsEnabled(): boolean {
        return this.companySettingsDictionary?.GPS_ENABLED?.value === 'true';
    }

    @computed
    public get webAREnabled(): boolean {
        return this.companySettingsDictionary?.WEB_AR_ENABLED?.value === 'true';
    }

    @computed
    public get switchWorkspacesEnabled(): boolean {
        return this.companySettingsDictionary?.SWITCH_WORKSPACES_ENABLED?.value === 'true';
    }

    @computed
    public get faceToFaceModeEnabled(): boolean {
        return this.companySettingsDictionary?.FACE_TO_FACE_MODE_ENABLED?.value === 'true';
    }

    @computed
    public get unreleasedFeaturesEnabled(): boolean {
        return this.companySettingsDictionary?.UNRELEASED_FEATURES_ENABLED?.value === 'true';
    }

    @computed
    public get roomOutcomeFeatureEnabled(): boolean {
        return this.companySettingsDictionary?.ROOM_OUTCOME_FEATURE_ENABLED?.value === 'true';
    }

    @computed
    public get screenshareEnabled(): boolean {
        return this.companySettingsDictionary?.SCREENSHARE_ENABLED?.value === 'true';
    }

    @computed
    public get videoTrackPreviewEnabled(): boolean {
        return this.companySettingsDictionary?.VIDEO_TRACK_PREVIEW_ENABLED?.value === 'true';
    }

    @computed
    public get expertAvailabilityEnabled(): boolean {
        return this.companySettingsDictionary?.EXPERT_AVAILABILITY_ENABLED?.value === 'true';
    }

    @computed
    public get callMetricsEnabled(): boolean {
        return this.companySettingsDictionary?.CALL_METRICS_ENABLED?.value === 'true';
    }

    @computed
    public get showCustomerRedirectUrlEnabled(): boolean {
        return this.companySettingsDictionary?.SHOW_CUSTOMER_REDIRECT_URL?.value === 'true';
    }

    @computed
    public get showCustomerCompanyWatermarkEnabled(): boolean {
        return this.companySettingsDictionary?.SHOW_CUSTOMER_COMPANY_WATERMARK?.value === 'true';
    }

    @computed
    public get showAnonymousShareEnabled(): boolean {
        return this.companySettingsDictionary?.SHOW_ANONYMOUS_SHARE_ENABLED?.value === 'true';
    }

    @computed
    public get showGpsEnabled(): boolean {
        return this.companySettingsDictionary?.SHOW_GPS_ENABLED?.value === 'true';
    }

    @computed
    public get postCallAnnotationsEnabled(): boolean {
        return this.companySettingsDictionary?.POST_CALL_ANNOTATIONS_ENABLED?.value === 'true';
    }

    @computed
    public get groupManagementEnabled(): boolean {
        return this.companySettingsDictionary?.GROUP_MANAGEMENT_ENABLED?.value === 'true';
    }

    @computed
    public get allowPauseResumeRecording(): boolean {
        return this.companySettingsDictionary?.ALLOW_PAUSE_RESUME_RECORDING?.value === 'true';
    }

    @computed
    public get transcriptionEnabled(): boolean {
        return this.companySettingsDictionary?.TRANSCRIPTION_ENABLED?.value === 'true';
    }

    @computed
    public get transcriptionUiEnabled(): boolean {
        return this.companySettingsDictionary?.TRANSCRIPTION_UI_ENABLED?.value === 'true';
    }

    @computed
    public get chimeLowBandwidthOptimizationEnabled(): boolean {
        return (
            this.companySettingsDictionary?.CHIME_LOW_BANDWIDTH_OPTIMIZATION_ENABLED?.value ===
            'true'
        );
    }

    @computed
    public get callLogFilterEnabled(): boolean {
        return this.companySettingsDictionary?.CALL_LOG_FILTER_ENABLED?.value === 'true';
    }

    @computed
    public get callDeactivateEnabled(): boolean {
        return this.companySettingsDictionary?.CALL_DEACTIVATE_ENABLED?.value === 'true';
    }

    @computed
    public get ahsVirtualExpertEnabled(): boolean {
        return this.companySettingsDictionary?.AHS_VIRTUAL_EXPERT_ENABLED?.value === 'true';
    }

    @computed
    public get preferredCodecs(): string {
        return this.companySettingsDictionary?.PREFERRED_CODECS?.value;
    }

    @computed
    public get roomNoteCharLimit(): number {
        return Number(this.companySettingsDictionary?.ROOM_NOTE_CHAR_LIMIT?.value || '1700');
    }

    @computed
    public get streemshotNoteCharLimit(): number {
        return Number(this.companySettingsDictionary?.STREEMSHOT_NOTE_CHAR_LIMIT?.value || '225');
    }

    @computed
    public get callDetailTabsEnabled(): boolean {
        return this.companySettingsDictionary?.CALL_DETAIL_TABS_ENABLED?.value === 'true';
    }

    @computed
    public get lostConnectionModalEnabled(): boolean {
        return this.companySettingsDictionary?.LOST_CONNECTION_MODAL_ENABLED?.value === 'true';
    }

    @computed
    public get sendNoteEnabled(): boolean {
        return this.companySettingsDictionary?.SEND_NOTE_ENABLED?.value === 'true';
    }

    @computed
    public get advancedMediaSettingsEnabled(): boolean {
        return this.companySettingsDictionary?.ADVANCED_MEDIA_SETTINGS_ENABLED?.value === 'true';
    }

    @computed
    public get editorNotesEnabled(): boolean {
        return this.companySettingsDictionary?.NOTES_EDITOR_ENABLED?.value === 'true';
    }

    @computed
    public get notesTemplatesEnabled(): boolean {
        return this.companySettingsDictionary?.NOTES_TEMPLATES_ENABLED?.value === 'true';
    }

    @computed
    public get hvacEligibleEnabled(): boolean {
        return this.companySettingsDictionary?.HVAC_ELIGIBLE_ENABLED?.value === 'true';
    }

    @computed
    public get loading(): boolean {
        return this.loadSettingsStore.loading;
    }

    @computed
    public get loadingMore(): boolean {
        return this.loadSettingsStore.loading;
    }

    public async updateSetting(name: string, value: string): Promise<void> {
        await StreemAPI.companies.saveCompanySetting(this.companyCode, name, {
            value,
        });
        await this.loadSettingsStore.refresh();
    }

    public async getSettingDefinition(
        name: string,
    ): Promise<APITypes.StreemApiSettingDefinition | undefined> {
        try {
            const response = await StreemAPI.companies.getSettingDefinition(this.companyCode, {
                settingName: name,
            });
            return response.settingDefinition;
        } catch (error) {
            appLogger.error(`Error fetching default value for setting ${name}:`, error);
            return undefined;
        }
    }

    public async refresh(): Promise<void> {
        await this.loadSettingsStore.refresh();
    }

    public loadSettings(): CancellablePromise<APITypes.StreemApiListCompanySettingsResponse> {
        return this.loadSettingsStore.fetch(this.companyCode);
    }

    public async loadDispositionConfigDetails(companyCodeOrSid = this.companyCode): Promise<void> {
        try {
            const roomOutcomeConfigResponse = await StreemAPI.companies.getCompanyRoomOutcomeConfig(
                companyCodeOrSid,
            );
            const data = await fetch(roomOutcomeConfigResponse.roomOutcomeConfigUrl);
            const options = await data.json();

            this._roomOutcomeConfig = {
                fileName: roomOutcomeConfigResponse.fileName.replace('.json', ''),
                ...options,
            };
        } catch (error) {
            this._roomOutcomeConfig = null;
            if (error instanceof APIError && error.status === 404) {
                appLogger.warn('Room outcome not configured', error);
            } else {
                throw error;
            }
        }
    }

    public get roomOutcomeConfig(): streem.api.ICompanyRoomOutcomeConfig &
        APITypes.StreemApiGetCompanyRoomOutcomeConfigResponse {
        return this._roomOutcomeConfig;
    }

    @computed
    private get companySettingsDictionary(): SettingsDictionary {
        if (!this.loadSettingsStore.response || !this.loadSettingsStore.response.settings) {
            return undefined;
        }
        return createCompanySettingsDictionary(this.loadSettingsStore.response.settings);
    }

    @computed
    public get sourceSettingsDictionary(): SourceSettingsDictionary {
        if (!this.loadSettingsStore.response || !this.loadSettingsStore.response.settings) {
            return {};
        }
        return createSourceSettingsDictionary(this.loadSettingsStore.response.settings);
    }

    private filterAndSortSettingsForSettingsPage(
        settings: APITypes.StreemApiSetting[],
        meta: DisplayableSettingsDictionary,
    ): Setting[] {
        return settings
            .filter(s => s.name && s.name in meta)
            .map(s => Object.assign({}, s, meta[s.name]) as Setting)
            .sort((a, b) => a.order - b.order);
    }

    private toDeveloperSetting(
        setting: APITypes.StreemApiSetting,
        index: number,
        meta?: SettingMeta,
    ): Setting {
        invariant(setting.name, `setting.name not populated: ${JSON.stringify(setting)}`);
        return {
            ...setting,
            name: setting.name,
            label: meta ? meta.label : setting.name,
            order: index,
            description: meta ? meta.description : '',
            testId: meta ? meta.testId : '',
        };
    }
}
